import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { ArrowRight, MapPin, Briefcase, CheckCircle, EnvelopeSimple, X } from "@phosphor-icons/react";
import { listCareerRoles, applyForRole } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Section, PageHero, EyebrowLabel, CalloutCard } from "@/components/marketing/MarketingUI";
import useSEO from "@/lib/useSEO";

export default function CareersPage() {
  useSEO({
    title: "Careers · Build the modern OS for mining resource work",
    description: "SRES is hiring senior geologists, backend engineers, mining software specialists and enterprise sales — small team, big problems.",
    keywords: "SRES careers, mining jobs, resource geologist, backend engineer",
    path: "/careers",
  });
  const [selectedRole, setSelectedRole] = useState(null);
  const { data, isLoading } = useQuery({
    queryKey: ["career-roles"],
    queryFn: listCareerRoles,
  });
  const roles = data?.roles || [];

  return (
    <div data-testid="careers-page">
      <PageHero
        eyebrow="Careers"
        title="Build the modern operating system for mining."
        description="SRES is hiring across geology, engineering, delivery and revenue. Small, senior team — big, meaningful problems."
        testid="careers-hero"
      />

      <Section className="py-20 md:py-24" testid="careers-list">
        <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
          <div>
            <EyebrowLabel className="mb-3">Open roles</EyebrowLabel>
            <h2 className="font-heading font-black text-slate-900 text-3xl md:text-4xl tracking-tight">
              {isLoading ? "Loading current openings…" : `${roles.length} open ${roles.length === 1 ? "position" : "positions"}`}
            </h2>
            <p className="mt-3 text-slate-600 max-w-2xl text-[15px]">
              Every role is remote-friendly with anchor days in Bengaluru or Bhubaneswar. Applications are read
              by the hiring manager — not a keyword filter.
            </p>
          </div>
          <a
            href="mailto:info@sresources.co?subject=Speculative%20application"
            className="inline-flex items-center gap-2 text-brand-blue text-sm font-medium hover:underline"
            data-testid="careers-speculative"
          >
            <EnvelopeSimple size={14} weight="bold" />
            Speculative application → info@sresources.co
          </a>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {roles.map((role) => (
            <div key={role.slug} className="p-7 rounded-2xl border border-slate-200 bg-white flex flex-col" data-testid={`role-card-${role.slug}`}>
              <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-brand-blue font-mono">
                <Briefcase size={12} weight="bold" /> {role.team}
              </div>
              <div className="mt-3 font-heading font-bold text-slate-900 text-2xl leading-tight">{role.title}</div>
              <div className="mt-2 flex items-center gap-4 text-[13px] text-slate-500">
                <span className="inline-flex items-center gap-1.5"><MapPin size={13} /> {role.location}</span>
                <span>· {role.type}</span>
              </div>
              <p className="mt-4 text-slate-600 text-[14px] leading-relaxed flex-1">{role.summary}</p>
              <div className="mt-6 pt-6 border-t border-slate-100 flex items-center justify-between gap-3">
                <button
                  onClick={() => setSelectedRole(role)}
                  className="text-slate-700 text-[13px] font-medium hover:text-brand-blue transition-colors"
                  data-testid={`role-view-${role.slug}`}
                >
                  View details →
                </button>
                <Button
                  onClick={() => setSelectedRole({ ...role, autoApply: true })}
                  className="h-10 px-5 bg-brand-blue hover:bg-brand-blueDeep text-white text-sm font-medium rounded-md"
                  data-testid={`role-apply-${role.slug}`}
                >
                  Apply <ArrowRight size={13} weight="bold" className="ml-2" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      </Section>

      <Section className="py-20 md:py-24 bg-[#F7F8FB]" testid="careers-why">
        <div className="grid grid-cols-1 lg:grid-cols-[1fr,1.4fr] gap-12">
          <div>
            <EyebrowLabel className="mb-4">Why SRES</EyebrowLabel>
            <h2 className="font-heading font-black text-slate-900 text-3xl md:text-4xl tracking-tight">
              A small team building infrastructure the mining world will use for a decade.
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <Perk title="Meaningful work" body="Every feature ships to real mine sites and real Competent Persons. Nothing you build gets shelved." />
            <Perk title="Senior peers only" body="No juniors, no interns. Every colleague is at the top of their craft." />
            <Perk title="Remote-friendly" body="Anchor days in Bengaluru or Bhubaneswar. Otherwise, work from wherever you are sharp." />
            <Perk title="Clear compensation" body="Above-market cash, transparent bands and long-vesting equity." />
          </div>
        </div>
      </Section>

      <Section className="py-16 md:py-24" testid="careers-cta">
        <CalloutCard
          eyebrow="Don't see your role?"
          title="If you want to work here, tell us how you'd move the needle."
          description="We open new roles quietly. A well-argued speculative application always gets read."
          cta="Send a speculative application"
          to="/contact"
          testid="careers-cta-callout"
        />
      </Section>

      <ApplyDialog
        role={selectedRole}
        onClose={() => setSelectedRole(null)}
      />
    </div>
  );
}

function Perk({ title, body }) {
  return (
    <div className="p-6 rounded-xl border border-slate-200 bg-white">
      <div className="font-heading font-bold text-slate-900 text-lg">{title}</div>
      <p className="mt-2 text-slate-600 text-[14px] leading-relaxed">{body}</p>
    </div>
  );
}

function ApplyDialog({ role, onClose }) {
  const [form, setForm] = useState({
    name: "", email: "", phone: "", experience_years: "", resume_url: "", cover_letter: "",
  });
  const [pending, setPending] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!role) {
      setForm({ name: "", email: "", phone: "", experience_years: "", resume_url: "", cover_letter: "" });
      setDone(false);
    }
  }, [role]);

  if (!role) return null;
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setPending(true);
    try {
      await applyForRole({
        role: role.title,
        name: form.name,
        email: form.email,
        phone: form.phone || null,
        experience_years: form.experience_years ? Number(form.experience_years) : null,
        resume_url: form.resume_url || null,
        cover_letter: form.cover_letter,
      });
      setDone(true);
      toast.success("Application received");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Failed to submit application");
    } finally {
      setPending(false);
    }
  };

  return (
    <Dialog open={!!role} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl bg-white max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="text-[11px] uppercase tracking-[0.22em] text-brand-blue font-mono">{role.team} · {role.location}</div>
          <DialogTitle className="font-heading text-2xl text-slate-900">{role.title}</DialogTitle>
          <DialogDescription className="text-slate-600 text-[14px]">{role.summary}</DialogDescription>
        </DialogHeader>

        {done ? (
          <div className="p-6 rounded-lg bg-emerald-50 border border-emerald-200 text-emerald-900" data-testid="apply-success">
            <div className="flex items-center gap-3">
              <CheckCircle size={22} weight="fill" className="text-emerald-600" />
              <div className="font-heading font-bold text-lg">Application received</div>
            </div>
            <p className="mt-2 text-emerald-800/80 text-[14px]">
              Thanks {form.name || "for applying"}. A hiring manager will reach out on <b>{form.email}</b> if there is a match. We
              try to reply to every applicant, even the ones we cannot take forward.
            </p>
            <button
              onClick={onClose}
              className="mt-4 inline-flex items-center gap-2 text-emerald-700 font-medium text-sm hover:underline"
              data-testid="apply-close"
            >
              <X size={14} /> Close
            </button>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-2">
              <div>
                <div className="text-[11px] uppercase tracking-[0.22em] text-slate-500 mb-2">What you will do</div>
                <ul className="space-y-2 text-slate-700 text-[13px] leading-relaxed">
                  {role.responsibilities.map((r) => (
                    <li key={r} className="flex items-start gap-2">
                      <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-brand-blue shrink-0" />
                      {r}
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <div className="text-[11px] uppercase tracking-[0.22em] text-slate-500 mb-2">What we are looking for</div>
                <ul className="space-y-2 text-slate-700 text-[13px] leading-relaxed">
                  {role.requirements.map((r) => (
                    <li key={r} className="flex items-start gap-2">
                      <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-brand-blue shrink-0" />
                      {r}
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <form onSubmit={submit} className="mt-6 pt-6 border-t border-slate-100 space-y-4" data-testid="apply-form">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <ApplyField label="Full name" required>
                  <Input value={form.name} onChange={set("name")} required minLength={2}
                    className="h-11 border-slate-200 focus-visible:ring-brand-blue" data-testid="apply-name" />
                </ApplyField>
                <ApplyField label="Email" required>
                  <Input type="email" value={form.email} onChange={set("email")} required
                    className="h-11 border-slate-200 focus-visible:ring-brand-blue" data-testid="apply-email" />
                </ApplyField>
                <ApplyField label="Phone">
                  <Input value={form.phone} onChange={set("phone")}
                    className="h-11 border-slate-200 focus-visible:ring-brand-blue" data-testid="apply-phone" />
                </ApplyField>
                <ApplyField label="Experience (years)">
                  <Input type="number" min="0" step="0.5" value={form.experience_years} onChange={set("experience_years")}
                    className="h-11 border-slate-200 focus-visible:ring-brand-blue" data-testid="apply-experience" />
                </ApplyField>
              </div>
              <ApplyField label="Resume URL (LinkedIn, Drive, etc.)">
                <Input type="url" value={form.resume_url} onChange={set("resume_url")} placeholder="https://…"
                  className="h-11 border-slate-200 focus-visible:ring-brand-blue" data-testid="apply-resume" />
              </ApplyField>
              <ApplyField label="Cover letter" required>
                <Textarea value={form.cover_letter} onChange={set("cover_letter")} rows={6} required minLength={8}
                  placeholder="Why this role, why SRES, and what you would ship in your first 90 days."
                  className="border-slate-200 focus-visible:ring-brand-blue" data-testid="apply-cover" />
              </ApplyField>
              <DialogFooter>
                <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>
                <Button type="submit" disabled={pending}
                  className="h-10 px-6 bg-brand-blue hover:bg-brand-blueDeep text-white font-medium rounded-md"
                  data-testid="apply-submit">
                  {pending ? "Submitting…" : "Submit application"}
                </Button>
              </DialogFooter>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function ApplyField({ label, required, children }) {
  return (
    <div>
      <Label className="text-[11px] uppercase tracking-[0.18em] text-slate-500">
        {label} {required ? <span className="text-brand-blue">*</span> : null}
      </Label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}
